/**
 * ExpectationsEditor.tsx — the fixture dossier's "Expected values" table
 * (2026-07-30 redesign: the old drawer stacked bordered inputs into a 720px
 * panel that clipped; this is a real table with room for every column).
 *
 * Reads as a table, edits like a form: inputs are borderless until hover/
 * focus, values monospace, zebra rows from the shared table tokens.
 */
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import type { ExpectationDraft } from '../../../services/appIntegrityService';

interface ExpectationsEditorProps {
  drafts: ExpectationDraft[];
  onPatch: (index: number, patch: Partial<ExpectationDraft>) => void;
  onRemove: (index: number) => void;
}

/** Borderless-until-focused input — table cell first, form field second. */
const cellInput = (mono: boolean) => ({
  variant: 'standard' as const,
  fullWidth: true,
  InputProps: {
    disableUnderline: false,
    sx: {
      fontSize: '0.82rem',
      ...(mono ? { fontFamily: 'monospace' } : {}),
      '&:before': { borderBottomColor: 'transparent' },
      '&:hover:not(.Mui-disabled):before': { borderBottomColor: 'divider' },
    },
  },
});

export const ExpectationsEditor = ({ drafts, onPatch, onRemove }: ExpectationsEditorProps) => {
  const theme = useTheme();
  return (
    <Table
      size="small"
      stickyHeader
      sx={{
        '& th': {
          backgroundColor: theme.table.headerBg,
          fontWeight: 700,
          fontSize: '0.72rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'text.secondary',
          whiteSpace: 'nowrap',
        },
        '& tbody tr:nth-of-type(even)': { backgroundColor: theme.table.stripeBg },
        '& td': { verticalAlign: 'top', py: 0.75 },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell sx={{ minWidth: 260 }}>Check / result path</TableCell>
          <TableCell sx={{ minWidth: 150 }} align="right">Expected</TableCell>
          <TableCell sx={{ width: 90 }} align="right">± abs</TableCell>
          <TableCell sx={{ width: 90 }} align="right">± rel</TableCell>
          <TableCell sx={{ width: 90 }}>Unit</TableCell>
          <TableCell sx={{ width: 44 }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {drafts.map((draft, index) => (
          <TableRow key={index} hover>
            <TableCell>
              <TextField
                {...cellInput(false)}
                placeholder="Label"
                value={draft.label}
                onChange={(e) => onPatch(index, { label: e.target.value })}
                InputProps={{
                  ...cellInput(false).InputProps,
                  sx: { ...cellInput(false).InputProps.sx, fontWeight: 600 },
                }}
              />
              <TextField
                {...cellInput(true)}
                placeholder="result.path[0].field"
                value={draft.resultPath}
                onChange={(e) => onPatch(index, { resultPath: e.target.value })}
                InputProps={{
                  ...cellInput(true).InputProps,
                  sx: {
                    ...cellInput(true).InputProps.sx,
                    fontSize: '0.72rem',
                    color: 'text.secondary',
                  },
                }}
              />
            </TableCell>
            <TableCell align="right">
              <TextField
                {...cellInput(true)}
                value={draft.expectedValue}
                onChange={(e) => onPatch(index, { expectedValue: e.target.value })}
                inputProps={{ style: { textAlign: 'right' } }}
              />
            </TableCell>
            <TableCell align="right">
              <TextField
                {...cellInput(true)}
                value={draft.toleranceAbs ?? ''}
                onChange={(e) =>
                  onPatch(index, { toleranceAbs: e.target.value === '' ? null : Number(e.target.value) })
                }
                inputProps={{ style: { textAlign: 'right' } }}
              />
            </TableCell>
            <TableCell align="right">
              <TextField
                {...cellInput(true)}
                value={draft.toleranceRel ?? ''}
                onChange={(e) =>
                  onPatch(index, { toleranceRel: e.target.value === '' ? null : Number(e.target.value) })
                }
                inputProps={{ style: { textAlign: 'right' } }}
              />
            </TableCell>
            <TableCell>
              <TextField
                {...cellInput(false)}
                value={draft.unit ?? ''}
                onChange={(e) => onPatch(index, { unit: e.target.value || null })}
              />
            </TableCell>
            <TableCell>
              <Tooltip title="Remove this check">
                <IconButton size="small" onClick={() => onRemove(index)}>
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
        {drafts.length === 0 && (
          <TableRow>
            <TableCell colSpan={6}>
              <Box sx={{ py: 2, textAlign: 'center', color: 'text.secondary', fontSize: '0.85rem' }}>
                No checks yet — add one to give this fixture a golden expectation.
              </Box>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};
